import { useNostr } from '@nostrify/react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { NSchema as n } from '@nostrify/nostrify';
import type { NostrEvent } from '@nostrify/nostrify';
import { MICROTONAL_KIND, type Keyboard, type MicrotonalNostrPayload } from '@/lib/microtonal/types';
import { useNostrPublish } from '@/hooks/useNostrPublish';

/** Parse a kind 37357 event into a Keyboard. */
export function parseKeyboardEvent(event: NostrEvent): Keyboard | null {
  try {
    const payload = n.json().pipe(n.object({
      kind: n.string(),
      keyboard: n.object({
        name: n.string(),
        scale: n.object({
          name: n.string(),
          periodCents: n.number(),
          steps: n.array(n.object({
            cents: n.number(),
            label: n.string().optional(),
            ratio: n.string().optional(),
          })),
          baseFrequency: n.number(),
        }),
        keys: n.array(n.object({
          id: n.string(),
          degree: n.number(),
          period: n.number(),
          keyLabel: n.string().optional(),
          color: n.string().optional(),
          noteName: n.string().optional(),
        })),
      }),
      v: n.number(),
    })).parse(event.content);

    // Reconstruct with proper types
    const keyboard = payload.keyboard as unknown as Keyboard;
    keyboard.id = `nostr-${event.id.slice(0, 16)}`;
    keyboard.scale.id = `nostr-scale-${event.id.slice(0, 16)}`;
    keyboard.keys.forEach((k) => {
      if (k.color === 'white' || k.color === 'black' || k.color === 'accent') {
        // keep
      } else {
        k.color = undefined;
      }
    });

    return keyboard;
  } catch {
    return null;
  }
}

/** Build a kind 37357 event content payload from a Keyboard. */
export function buildKeyboardPayload(keyboard: Keyboard): MicrotonalNostrPayload {
  const { id, builtIn, ...rest } = keyboard;
  void id;
  void builtIn;
  return {
    kind: 'keyboard',
    keyboard: rest,
    v: 1,
  };
}

/** Hook to query shared microtonal keyboards from Nostr. */
export function useSharedKeyboards() {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['nostr', 'microtonal', 'keyboards'],
    queryFn: async (c) => {
      const events = await nostr.query(
        [{ kinds: [MICROTONAL_KIND], limit: 50 }],
        { signal: c.signal }
      );
      
      const keyboards: { event: NostrEvent; keyboard: Keyboard }[] = [];
      for (const event of events) {
        const keyboard = parseKeyboardEvent(event);
        if (keyboard) {
          keyboards.push({ event, keyboard });
        }
      }
      return keyboards;
    },
    staleTime: 60_000,
  });
}

/** Hook to publish a keyboard to Nostr as kind 37357. */
export function usePublishKeyboard() {
  const { mutateAsync: publish } = useNostrPublish();

  return useMutation({
    mutationFn: async ({ keyboard, dTag }: { keyboard: Keyboard; dTag: string }) => {
      const payload = buildKeyboardPayload(keyboard);
      const content = JSON.stringify(payload);
      
      const event = await publish({
        kind: MICROTONAL_KIND,
        content,
        tags: [
          ['d', dTag],
          ['alt', `Microtonal keyboard: ${keyboard.name}`],
          ['name', keyboard.name],
          ['t', 'microtonal'],
          ['t', 'music'],
          ['t', 'tuning'],
          ['period', String(keyboard.scale.periodCents)],
          ['degrees', String(keyboard.scale.steps.length + 1)],
        ],
      });
      
      return event;
    },
  });
}
